import { useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { message } from 'antd'
import { authService } from '../services/authService'
import { tokenRefreshService } from '../services/tokenRefreshService'
import { idleDetectionService } from '../services/idleDetectionService'
import { stackService } from '../services/stackService'
import { hasPlan, hasGlobalRole, GlobalRoles } from '../utils/roleUtils'
import type { User } from '../types/milestone.types'

/**
 * Authentication State Machine States
 *
 * Flow:
 * 1. initializing: Checking if we have a session (sessionStorage hint or OAuth callback)
 * 2. authenticating: OAuth flow in progress (redirecting or processing callback)
 * 3. authenticated: Valid user session with data
 * 4. unauthenticated: No session or session expired
 *
 * Transitions:
 * - initializing → authenticated (session valid)
 * - initializing → unauthenticated (no session)
 * - unauthenticated → authenticating (login initiated)
 * - authenticating → authenticated (OAuth success)
 * - authenticating → unauthenticated (OAuth error)
 * - authenticated → unauthenticated (logout or session expired)
 */
export type AuthState = 'initializing' | 'authenticating' | 'authenticated' | 'unauthenticated'

/**
 * Auth Hook Return Type
 */
export interface UseAuthReturn {
  // State
  state: AuthState
  user: User | null
  isLoading: boolean
  error: Error | null

  // Computed
  isAuthenticated: boolean
  isInitializing: boolean
  isAuthenticating: boolean

  // Actions
  initiateLogin: () => Promise<void>
  logout: (reason?: 'manual' | 'session_timeout' | 'idle_timeout') => Promise<void>
  switchOrg: (orgId: string) => Promise<void>
}

/**
 * Check if user might be authenticated (sessionStorage hint or OAuth callback)
 * This determines whether to call the /me endpoint or not
 */
function mightHaveSession(): boolean {
  // Check if OAuth callback just succeeded
  const urlParams = new URLSearchParams(window.location.search)
  const isOAuthCallback = urlParams.get('success') === 'true'

  // Check sessionStorage hint (set after successful login)
  const hasSessionHint = sessionStorage.getItem('isAuthenticated') === 'true'

  return isOAuthCallback || hasSessionHint
}

/**
 * Detect if current URL is an OAuth callback (success or error)
 */
function isOAuthCallback(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has('success') || urlParams.has('error')
}

/**
 * State Machine-based Authentication Hook
 *
 * Consolidates ALL auth state into a single hook with clear state transitions.
 * Automatically handles OAuth callbacks, session management, and service initialization.
 *
 * Features:
 * - Clear state machine: initializing → authenticating → authenticated/unauthenticated
 * - OAuth callback detection and processing (automatic)
 * - Plan checking with redirect to login (no_plan=true)
 * - Token refresh service initialization
 * - Idle detection service initialization
 * - Stack service initialization based on user email
 * - Prevents login page flash during route transitions (keepPreviousData)
 *
 * Usage:
 * ```tsx
 * function App() {
 *   const { state, user, isAuthenticated, initiateLogin, logout } = useAuth()
 *
 *   // Show loading during initialization
 *   if (state === 'initializing' || state === 'authenticating') {
 *     return <LoadingScreen />
 *   }
 *
 *   // Show login page when not authenticated
 *   if (state === 'unauthenticated') {
 *     return <LoginPage onLogin={initiateLogin} />
 *   }
 *
 *   // Show app when authenticated
 *   return <Dashboard user={user!} onLogout={logout} />
 * }
 * ```
 *
 * @returns Auth state and actions
 */
export function useAuth(): UseAuthReturn {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Detect if we should fetch user info (sessionStorage hint or OAuth callback)
  const shouldFetch = mightHaveSession()

  // Detect if we're processing an OAuth callback
  const isProcessingCallback = isOAuthCallback()

  // React Query for user data
  const {
    data: user,
    isLoading: userLoading,
    isFetching: userFetching,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await authService.getUserInfo()

      // 401 means session expired or never existed
      if (!response.success) {
        // Only clear sessionStorage for explicit auth failures (401)
        // NOT for network errors or temporary failures - keeps session hint sticky
        const isAuthError = response.errors?.some(e =>
          e.code === 'UNAUTHORIZED' || e.code === 'GET_USER_INFO_FAILED'
        )
        if (isAuthError) {
          sessionStorage.removeItem('isAuthenticated')
          console.log('📭 No active session - cleared hint')
        } else {
          console.log('📭 /me failed but keeping session hint (may be temporary)')
        }
        return null
      }

      if (!response.data) {
        console.warn('⚠️ getUserInfo succeeded but returned no data')
        return null
      }

      // Success! Set sessionStorage hint for future page loads
      sessionStorage.setItem('isAuthenticated', 'true')
      console.log('✅ User authenticated:', response.data.email)

      // NOTE: Service initialization (token refresh, idle detection) is handled
      // in a separate useEffect that watches state transitions, not here.
      // This keeps queryFn pure and prevents re-initialization on every refetch.

      return response.data
    },
    enabled: shouldFetch,                // Only run if we think there's a session
    staleTime: 1000 * 60 * 5,            // 5 minutes - user info changes occasionally
    gcTime: 1000 * 60 * 30,              // 30 minutes - keep in cache while active
    refetchOnWindowFocus: true,          // Refetch when user returns to tab
    refetchOnMount: true,                // Always check on component mount
    retry: false,                        // Don't retry - let OAuth callback trigger refetch
    retryOnMount: true,                  // Retry when component remounts (after OAuth)
    placeholderData: keepPreviousData,   // CRITICAL: Prevents login flash during navigation
  })

  // OAuth callback processing effect
  useEffect(() => {
    if (!isProcessingCallback) return

    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')

    console.log('🔍 OAuth callback detected:', { success, error })

    if (success === 'true') {
      console.log('✅ OAuth callback successful - backend set cookies')

      // Set sessionStorage hint BEFORE refetch so query is enabled
      sessionStorage.setItem('isAuthenticated', 'true')

      // Clean up URL immediately to prevent re-processing
      window.history.replaceState({}, document.title, window.location.pathname)

      // Trigger refetch to get user data
      console.log('🔄 Refetching user data after OAuth success...')
      refetchUser().then(async ({ data: userData }) => {
        // Check plan status for fresh logins
        // Internal staff (rp_admin, rp_support) bypass plan requirement
        const isInternalStaff = hasGlobalRole(userData, [GlobalRoles.RP_ADMIN, GlobalRoles.RP_SUPPORT])
        if (userData && !hasPlan(userData) && !isInternalStaff) {
          console.log('⚠️  No plan detected - redirecting to login with no_plan flag')

          // Clear auth state
          sessionStorage.removeItem('isAuthenticated')
          queryClient.clear()

          // Navigate to login with no_plan flag
          navigate('/login?no_plan=true', { replace: true })
          return
        }

        // Handle missing org context after login
        if (userData && !userData.currentOrganization?.id) {
          if (isInternalStaff) {
            // Internal staff must pick an org from admin panel
            console.log('⚠️  Internal staff with no org - redirecting to org switch')
            navigate('/rp-admin/org-switch', { replace: true })
            return
          }

          const orgs = userData.organizations || []

          if (orgs.length === 0) {
            // No orgs available - log out with explanation
            console.log('⚠️  No organizations available - logging out')
            sessionStorage.removeItem('isAuthenticated')
            queryClient.clear()
            navigate('/login?no_org=true', { replace: true })
            return
          }

          // Auto-switch to first org
          const targetOrg = orgs[0]
          console.log(`🔄 Auto-switching to org: ${targetOrg.name} (${targetOrg.id})`)

          try {
            const switchResult = await authService.switchOrganization(targetOrg.id)
            if (switchResult.success) {
              // Show notification only if user has multiple orgs
              if (orgs.length > 1) {
                message.info(`You're now working in ${targetOrg.name}. Switch organizations anytime from your profile menu.`)
              }
              // Refetch user data with new org context
              await refetchUser()
            } else {
              console.error('❌ Auto org switch failed:', switchResult.errors)
              message.error('Failed to set up your organization. Please try again.')
            }
          } catch (error) {
            console.error('❌ Auto org switch error:', error)
            message.error('Failed to set up your organization. Please try again.')
          }
        }
      })
    } else if (error) {
      console.error('❌ OAuth callback error:', error)
      sessionStorage.removeItem('isAuthenticated')

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)

      // Navigate to login with error
      navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true })
    }
  }, [isProcessingCallback, refetchUser, queryClient, navigate])

  /**
   * Determine current auth state based on data and loading states
   */
  const state = useMemo<AuthState>(() => {
    // Processing OAuth callback
    if (isProcessingCallback) {
      return 'authenticating'
    }

    // Loading user data (first load or refetching with no previous data)
    if ((userLoading || userFetching) && !user) {
      return 'initializing'
    }

    // Have user data - authenticated
    if (user && !userError) {
      return 'authenticated'
    }

    // No user data and not loading - unauthenticated
    if (!user && !userLoading && !userFetching) {
      return 'unauthenticated'
    }

    // Error state - treat as unauthenticated
    if (userError) {
      return 'unauthenticated'
    }

    // Default to initializing if unclear
    return 'initializing'
  }, [user, userLoading, userFetching, userError, isProcessingCallback])

  /**
   * Service lifecycle management effect
   *
   * Initializes services when transitioning TO authenticated state,
   * and destroys services when transitioning AWAY from authenticated state.
   * This ensures services are initialized exactly once per auth session,
   * not on every React Query refetch.
   *
   * Uses React's cleanup function pattern to ensure services are destroyed:
   * - When component unmounts (even if still authenticated)
   * - When state transitions from authenticated to unauthenticated
   * - When user object changes (cleanup before re-init)
   */
  useEffect(() => {
    // Only run when authenticated with user data
    if (state !== 'authenticated' || !user) {
      return // No initialization needed, no cleanup to return
    }

    // Initialize token refresh service if we have token expiry
    if (user.tokenExpiry) {
      console.log('🔄 Initializing token refresh service with expiry:', user.tokenExpiry)
      tokenRefreshService.initialize(user.tokenExpiry)
    } else {
      console.warn('⚠️  No tokenExpiry in user data - token refresh will use fallback')
    }

    // Initialize idle detection service
    const isInternalStaff = hasGlobalRole(user, [GlobalRoles.RP_ADMIN, GlobalRoles.RP_SUPPORT])
    console.log('⏱️  Initializing idle detection service')
    idleDetectionService.initialize({ isInternalStaff })

    // Initialize stack selection based on user email
    stackService.initializeStackForUser(user.email)

    // Cleanup function - runs on unmount or before next effect execution
    return () => {
      console.log('🧹 Cleaning up services (unmount or state change)')
      tokenRefreshService.destroy()
      idleDetectionService.destroy()
    }
  }, [state, user])

  /**
   * Initiate login via Cognito Hosted UI
   * Redirects to Cognito, which redirects to backend, which redirects back to frontend
   */
  const initiateLogin = useCallback(async () => {
    console.log('🚀 Initiating login via Hosted UI...')
    const result = await authService.signInWithHostedUI()

    if (!result.success) {
      console.error('❌ Failed to initiate login:', result.errors)
      // Error will be shown by errorService in authService
    }
    // If successful, user will be redirected to Cognito
  }, [])

  /**
   * Logout user and optionally clear org context for internal users
   *
   * @param reason - Reason for logout (manual, session_timeout, idle_timeout)
   */
  const logout = useCallback(async (reason: 'manual' | 'session_timeout' | 'idle_timeout' = 'manual') => {
    console.log('🔴 LOGOUT: Starting logout process...', { reason })

    // For rp_admin/rp_support users, clear org context before logout
    // This prevents them from accidentally being in a client's org on next login
    if (user && hasGlobalRole(user, [GlobalRoles.RP_ADMIN, GlobalRoles.RP_SUPPORT])) {
      console.log('🔴 LOGOUT: Internal user detected - clearing org context first')
    }

    // Build logout path with reason
    let logoutPath = '/auth/logout'
    if (reason !== 'manual') {
      logoutPath += `?reason=${reason}`
      // TODO: Add idle_duration parameter if idle_timeout
    }

    // Clear sessionStorage hint BEFORE signOut to prevent /me call during redirect
    sessionStorage.removeItem('isAuthenticated')

    // Clear React Query cache
    queryClient.clear()

    // Destroy services
    tokenRefreshService.destroy()
    idleDetectionService.destroy()

    // Call backend to clear cookies and redirect to Cognito logout
    await authService.signOut(logoutPath)

    // authService.signOut() will redirect to Cognito logout
    // which will redirect back to /login
  }, [user, queryClient])

  /**
   * Switch organization context
   * Calls backend to update cookies with new org context
   *
   * @param orgId - Organization ID to switch to
   */
  const switchOrg = useCallback(async (orgId: string) => {
    if (!user) {
      console.error('❌ Cannot switch org - no user authenticated')
      return
    }

    const newOrg = user.organizations.find(org => org.id === orgId)
    if (!newOrg) {
      console.error('❌ Cannot switch org - org not found in user organizations')
      return
    }

    console.log('🔄 Switching organization to:', newOrg.name)

    try {
      // Call backend to switch org context and update cookies
      const result = await authService.switchOrganization(orgId)

      if (!result.success) {
        console.error('❌ Failed to switch organization:', result.errors)
        // Error already shown by errorService
        return
      }

      // Backend has updated cookies with new org context
      // Clear all caches - tenant data is completely different
      console.log('🧹 Clearing all caches for tenant switch...')
      queryClient.clear()

      // Navigate to home page - this unmounts/remounts components with fresh queries
      console.log('✅ Organization switched to:', newOrg.name)
      console.log('🏠 Navigating to home to load new org data...')
      navigate('/')
    } catch (error) {
      console.error('❌ Organization switch error:', error)
    }
  }, [user, queryClient, navigate])

  // Return auth state and actions
  return {
    // State
    state,
    user: user ?? null,
    isLoading: userLoading || userFetching,
    error: userError,

    // Computed
    isAuthenticated: state === 'authenticated',
    isInitializing: state === 'initializing',
    isAuthenticating: state === 'authenticating',

    // Actions
    initiateLogin,
    logout,
    switchOrg
  }
}

export default useAuth
